import { Icon } from '@iconify/react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { InputPrefix, InputSuffix, TextBox, TextBoxChangeEvent } from '@progress/kendo-react-inputs';
import { Label } from '@progress/kendo-react-labels';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import useGuideMe from 'src/hooks/useGuideMe';
import { useContextSelector } from 'use-context-selector';
import useShoppingCartGuideMeSteps from './GuideMeSteps';
import { ShoppingCartContext } from './ShoppingCartContext';
import ShoppingCartSelectedTotals from './ShoppingCartSelectedTotals';

const ShoppingCartFilters = () => {
  const { t } = useTranslation();

  const filterSearch = useContextSelector(ShoppingCartContext, (context) => context.filterSearch);
  const handleFilterSearchChange = useContextSelector(
    ShoppingCartContext,
    (context) => context.handleFilterSearchChange
  );
  const hasSelectedProductsForPo = useContextSelector(
    ShoppingCartContext,
    (context) => context.hasSelectedProductsForPo
  );
  const handleCreatePoForSelected = useContextSelector(
    ShoppingCartContext,
    (context) => context.handleCreatePoForSelected
  );
  const isCreatePoActionInProgress = useContextSelector(
    ShoppingCartContext,
    (context) => context.isCreatePoActionInProgress
  );
  const handleReviewContractedAlternatives = useContextSelector(
    ShoppingCartContext,
    (context) => context.handleReviewContractedAlternatives
  );
  const hasNotRecommendedCartItems = useContextSelector(
    ShoppingCartContext,
    (context) => context.hasNotRecommendedCartItems
  );
  const canCreatePoForSelected = hasSelectedProductsForPo;

  const guideMeSteps = useShoppingCartGuideMeSteps();
  const { guideMe } = useGuideMe({
    steps: guideMeSteps,
    guideId: 'shopping-cart-guide-me',
  });

  const handleClearSearch = useCallback(() => {
    handleFilterSearchChange('');
  }, [handleFilterSearchChange]);

  return (
    <div className="shopping-cart-filters mb-2 mt-4 flex w-full min-w-0 flex-col gap-4 px-4 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-x-4 lg:gap-y-0">
      <div className="min-w-0 w-full max-w-[350px] justify-self-start lg:col-start-1">
        <FieldWrapper className="flex flex-row space-x-2">
          <Label
            editorId="shopping-cart-filter-input"
            className="flex shrink-0 items-center whitespace-nowrap text-xs font-semibold"
          >
            {t('Filter By')}
          </Label>
          <TextBox
            id="shopping-cart-filter-input"
            max={500}
            maxLength={500}
            size="medium"
            style={{ fontSize: '14px' }}
            placeholder={t('Item# or Description')}
            width="100%"
            value={filterSearch}
            onChange={(e: TextBoxChangeEvent) => {
              handleFilterSearchChange(String(e.value ?? ''));
            }}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            prefix={() => (
              <InputPrefix className="pl-2">
                <Icon height="18" width="18" color="#AAAAAA" icon="lucide:search" />
              </InputPrefix>
            )}
            suffix={() => (
              <InputSuffix className="pr-2">
                {filterSearch ? (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    aria-label={t('clearSearch')}
                    className="flex items-center justify-center"
                  >
                    <Icon height="16" width="16" color="#AAAAAA" icon="lucide:x" />
                  </button>
                ) : null}
              </InputSuffix>
            )}
          />
        </FieldWrapper>
      </div>

      <div className="shopping-cart-filters__action-row flex flex-wrap items-center justify-center gap-2 lg:contents">
        <div className="shopping-cart-filters__actions flex shrink-0 flex-wrap items-center justify-center gap-2 lg:col-start-2 lg:justify-self-center">
          <ShoppingCartSelectedTotals />

          <PopUp
            popupAlign={{ horizontal: 'center', vertical: 'top' }}
            anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
            className="max-h-[180px]"
            content={
              <div className="m-1 max-w-[350px]">
                <PopUpContentWrapper
                  type="Information"
                  contents={[t('Please select at least one product')]}
                />
              </div>
            }
            show={!canCreatePoForSelected}
          >
            <Button
              color="success"
              size="lg"
              className="shrink-0"
              disabled={!canCreatePoForSelected || isCreatePoActionInProgress}
              loading={isCreatePoActionInProgress}
              onClick={() => {
                void handleCreatePoForSelected();
              }}
            >
              {t('Create PO for Selected')}
            </Button>
          </PopUp>
        </div>

        <div className="shopping-cart-filters__secondary flex shrink-0 flex-wrap items-center justify-center gap-2 lg:col-start-3 lg:justify-self-end lg:justify-end">
          {hasNotRecommendedCartItems && (
            <PopUp
              popupAlign={{ horizontal: 'center', vertical: 'top' }}
              anchorAlign={{ horizontal: 'center', vertical: 'bottom' }}
              className="max-h-[180px]"
              content={
                <div className="m-1 max-w-[350px]">
                  <PopUpContentWrapper
                    type="Information"
                    contents={[t('contractedAlternativesNotFound')]}
                  />
                </div>
              }
              show={!hasNotRecommendedCartItems}
            >
              <Button color="info" size="lg" onClick={handleReviewContractedAlternatives}>
                {t('Review all Contracted Alternative(s)')}
              </Button>
            </PopUp>
          )}
          {guideMe}
        </div>
      </div>
    </div>
  );
};

export default ShoppingCartFilters;
